'use client'

import type { SurveyAnswer, SurveyQuestion } from '@/types/interactiveVideo'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { pickForcedQuestion, pickOptInQuestion, sortSurveyQuestions, surveyProgress } from './surveyCue'

export interface UseSurveySheetParams {
  /** 現在ノードのアンケート設問（提示タイミング付き） */
  questions: SurveyQuestion[]
  /** ノードの識別キー。切り替わり時にシート状態をリセットするために使う */
  nodeKey: string
  /** 現在の再生位置（秒） */
  currentTime: number
  /** 動画が末尾まで再生されたか（null-cue 設問の提示判定） */
  videoEnded: boolean
  /** 動画の長さ（秒）。cueTime が動画長を超える設問を末尾提示へ倒す（ADR-0015） */
  videoDuration?: number | null
  /** 回答済みの questionId 集合（視聴セッション単位・ノードをまたいで保持） */
  answeredIds: ReadonlySet<string>
  /** 回答確定時の処理（回答済み登録＋APIへ保存は呼び出し側が担う） */
  onAnswer: (question: SurveyQuestion, answer: SurveyAnswer) => void
  /** 動画を一時停止する */
  pauseVideo: () => void
  /** 動画の再生を再開する */
  resumeVideo: () => void
}

export interface SurveySheetState {
  /** シートが展開中か（展開中は動画を縮小＋CTAボタンと排他） */
  expanded: boolean
  /** 展開中に表示している設問。未展開なら null */
  activeQuestion: SurveyQuestion | null
  /** 必須設問（強制ポーズ・閉じるボタンを出さない） */
  isForced: boolean
  /** 「回答はこちら↓」オプトインタブを表示するか（任意設問・動画継続中） */
  showTab: boolean
  /** タブで案内している任意設問 */
  tabQuestion: SurveyQuestion | null
  /** アンケート進捗（回答済み数 / 総設問数） */
  progress: { answered: number; total: number }
  /** 提示中設問の番号（cue 提示順での位置・1始まり）。進捗「n/N」の n 表示に使う */
  currentIndex: number
  /** 提示中設問の既存回答（連続提示中に「戻る」で開き直したときの復元用）。無ければ null */
  activeAnswer: SurveyAnswer | null
  /** 提示順が前の回答済み設問へ戻れるか */
  canGoBack: boolean
  /** オプトインタブをタップしてシートを開く */
  onOpenTab: () => void
  /** 回答（SINGLE/MULTI/FREE）を確定する */
  onSubmitAnswer: (answer: SurveyAnswer) => void
  /** 任意設問のシートを閉じる（回答せず継続） */
  onCloseSheet: () => void
  /** 提示順が前の回答済み設問へ戻る（回答の修正用） */
  onBack: () => void
}

/**
 * 視聴プレイヤーのアンケートシート（Pattern D）の状態機構。
 *
 * - 必須設問: cue 到達で強制的にシート展開＋一時停止（回答するまで閉じられない）。
 *   末尾 null-cue は動画末尾で展開。
 * - 任意設問: cue 到達で「回答はこちら↓」オプトインタブを出す（動画は継続）。
 *   タップで展開＋一時停止。閉じると再生再開。
 * - 回答確定で回答済みに登録し、シートを閉じて再生再開→次の設問へ。
 *
 * 提示対象の選択は純粋関数（surveyCue.ts）に委譲し、ここでは
 * 展開状態・動画制御の副作用のみを管理する。
 */
export function useSurveySheet({
  questions,
  nodeKey,
  currentTime,
  videoEnded,
  videoDuration,
  answeredIds,
  onAnswer,
  pauseVideo,
  resumeVideo,
}: UseSurveySheetParams): SurveySheetState {
  const [expanded, setExpanded] = useState(false)
  const [activeQuestionId, setActiveQuestionId] = useState<string | null>(null)
  // 必須設問を「設問ごとに1回だけ」自動展開するためのガード
  const lastForcedIdRef = useRef<string | null>(null)
  // このシートを開いてから送った設問の順路。「戻る」で1つ前へ返すために持つ。
  // 連続提示（同時に due になった設問）でだけ増えるので、cue が分かれていて
  // 1問ずつ出るケースでは常に1件＝戻るボタンは出ない。
  const [history, setHistory] = useState<string[]>([])
  // 回答内容（questionId → 回答）。戻ったときに前回の選択を復元するために保持する。
  // 視聴セッション内のメモリのみ（回答自体はサーバへ都度保存され、再回答は上書きされる）。
  const [answers, setAnswers] = useState<ReadonlyMap<string, SurveyAnswer>>(() => new Map())

  const cueInput = { questions, currentTime, answeredIds, videoEnded, videoDuration }
  const forced = pickForcedQuestion(cueInput)
  const optIn = pickOptInQuestion(cueInput)

  // ノード切り替え時にシート状態をリセットする（回答済み集合はセッション単位で保持）
  useEffect(() => {
    setExpanded(false)
    setActiveQuestionId(null)
    lastForcedIdRef.current = null
    setHistory([])
  }, [nodeKey])

  // 必須設問が提示対象になったら強制的にシートを展開＋一時停止する
  useEffect(() => {
    if (forced && lastForcedIdRef.current !== forced.id) {
      lastForcedIdRef.current = forced.id
      setActiveQuestionId(forced.id)
      setHistory([forced.id])
      setExpanded(true)
      pauseVideo()
    } else if (!forced) {
      lastForcedIdRef.current = null
    }
  }, [forced, pauseVideo])

  const activeQuestion = useMemo(
    () => questions.find((q) => q.id === activeQuestionId) ?? null,
    [questions, activeQuestionId],
  )
  const sortedQuestions = useMemo(() => sortSurveyQuestions(questions), [questions])
  const previousAnsweredQuestionId = useMemo(() => {
    if (activeQuestionId == null) return null

    const activeIndex = sortedQuestions.findIndex((question) => question.id === activeQuestionId)
    for (let index = activeIndex - 1; index >= 0; index -= 1) {
      if (answeredIds.has(sortedQuestions[index].id)) return sortedQuestions[index].id
    }
    return null
  }, [activeQuestionId, answeredIds, sortedQuestions])

  const onOpenTab = useCallback(() => {
    if (!optIn) return
    setActiveQuestionId(optIn.id)
    setHistory([optIn.id])
    setExpanded(true)
    pauseVideo()
  }, [optIn, pauseVideo])

  const onSubmitAnswer = useCallback(
    (answer: SurveyAnswer) => {
      const question = questions.find((q) => q.id === activeQuestionId)
      if (!question) return
      onAnswer(question, answer)
      // 「戻る」で開き直したときに前回の選択を復元できるよう内容を控える
      setAnswers((prev) => new Map(prev).set(question.id, answer))

      // 同じタイミングで提示条件を満たしている次の設問があれば、シートを閉じずに
      // そのまま送る（進捗ドットの n/N と挙動を揃える）。閉じてしまうと、末尾提示など
      // 設問が同時に due になるケースで「回答 → 閉じる → タブを押し直す」を設問の数だけ
      // 繰り返させることになる。cue が分かれている設問は due にならないので、
      // 「その場面まで見てから聞く」従来の挙動は変わらない。
      //
      // 必須へは任意からも送る（どのみち直後に強制展開されるので、閉じる→即再展開の
      // ちらつきを避ける）。逆に必須から任意へは送らない ＝ 任意設問を半強制的に
      // 見せないため、いったん閉じてオプトインタブでの案内に戻す。
      //
      // answeredIds の更新は呼び出し側（onAnswer）が行うためこの時点では未反映。
      // いま回答した設問を加えた集合で次を選ぶ。
      const answeredWithCurrent = new Set(answeredIds)
      answeredWithCurrent.add(question.id)
      const nextInput = {
        questions,
        currentTime,
        answeredIds: answeredWithCurrent,
        videoEnded,
        videoDuration,
      }
      const next =
        pickForcedQuestion(nextInput) ?? (question.required ? null : pickOptInQuestion(nextInput))
      if (next) {
        // 必須の自動展開ガードも進めておく（同じ設問でもう一度 pauseVideo を呼ばない）
        if (next.required) lastForcedIdRef.current = next.id
        setActiveQuestionId(next.id)
        // 戻った先で答え直した場合は、その先の順路を捨てて積み直す
        setHistory((prev) => {
          const i = prev.indexOf(question.id)
          const base = i >= 0 ? prev.slice(0, i + 1) : prev
          return [...base, next.id]
        })
        return
      }

      setExpanded(false)
      setActiveQuestionId(null)
      setHistory([])
      resumeVideo()
    },
    [
      questions,
      activeQuestionId,
      onAnswer,
      resumeVideo,
      answeredIds,
      currentTime,
      videoEnded,
      videoDuration,
    ],
  )

  const onCloseSheet = useCallback(() => {
    setExpanded(false)
    setActiveQuestionId(null)
    setHistory([])
    resumeVideo()
  }, [resumeVideo])

  // 1つ前の回答済み設問へ戻る（シートは開いたまま・動画も止めたまま）。
  // cue が異なる設問は現在のシート履歴に残らないため、履歴に無ければ提示順と
  // answeredIds から直前の回答済み設問を探す。
  // 回答済みの設問へ戻るだけなので、選び直せばサーバ側で上書き保存される
  // （SurveyResponseService: 同一 sessionId × questionId × targetKey を delete してから insert）。
  const onBack = useCallback(() => {
    setActiveQuestionId((current) => {
      if (current == null) return current
      const i = history.indexOf(current)
      return i > 0 ? history[i - 1] : (previousAnsweredQuestionId ?? current)
    })
  }, [history, previousAnsweredQuestionId])

  const progress = surveyProgress(questions, answeredIds)

  // 提示中設問の cue 提示順での位置（1始まり）。スキップされた任意設問があっても
  // 「今何問目か」を正しく表すため、回答数ではなく提示順の位置で数える。
  const activeIndex = activeQuestion ? sortedQuestions.findIndex((q) => q.id === activeQuestion.id) + 1 : 0

  return {
    expanded,
    activeQuestion,
    isForced: activeQuestion?.required ?? false,
    showTab: !expanded && optIn != null,
    tabQuestion: optIn,
    progress,
    currentIndex: activeIndex,
    activeAnswer: activeQuestionId != null ? (answers.get(activeQuestionId) ?? null) : null,
    canGoBack:
      activeQuestionId != null &&
      (history.indexOf(activeQuestionId) > 0 || previousAnsweredQuestionId != null),
    onOpenTab,
    onSubmitAnswer,
    onCloseSheet,
    onBack,
  }
}
